import type { Field } from "./graphFieldsExtractor";

/**
 * Save-path hardening (roadmap §1.3 / Phase 2.0).
 *
 * The node-form PUTs the ENTIRE form state, and hydration seeds every
 * non-media/non-boolean field to `currentValue ?? ""`. Once nullable number /
 * enum / color columns are exposed (Phase 3/4) that becomes dangerous — all
 * live-verified against Strapi v4.20:
 *   - a nullable NUMBER or ENUM sent as "" (or NaN) → 400, rejecting the WHOLE
 *     PUT, so "open form → Save" fails;
 *   - a nullable STRING/COLOR sent as "" silently overwrites the value the VR
 *     editor wrote with an empty string.
 *
 * This normalizes the payload BEFORE it reaches the PLD builder + content PUT:
 *   - number : empty / NaN → omit; otherwise coerce to a real number;
 *   - select : unset ("", null, or Dropdown's "0" placeholder) → omit;
 *   - text / longtext / color : an untouched, hydration-seeded "" → omit (so we
 *     never overwrite a real value with ""); a user-cleared "" (dirty) is kept.
 * Every other key (media, booleans, bespoke arrays, name, is_start_scene, …)
 * passes through untouched.
 */
export const sanitizeChangesForSave = (
  changes: Record<string, any>,
  fields: Field[],
  dirtyFields: Record<string, unknown>,
): Record<string, any> => {
  const byName = new Map<string, Field>(fields.map((f) => [f.fieldName, f]));
  const out: Record<string, any> = {};

  for (const [key, value] of Object.entries(changes)) {
    const field = byName.get(key);
    if (!field) {
      out[key] = value;
      continue;
    }

    switch (field.type) {
      case "number": {
        if (value === "" || value === null || value === undefined) continue;
        const n = typeof value === "number" ? value : Number(value);
        if (Number.isNaN(n)) continue;
        out[key] = n;
        break;
      }
      case "select": {
        if (value === "" || value === null || value === undefined) continue;
        if (value === "0") {
          // "0" is Dropdown's injected placeholder sentinel — skip it unless it's
          // a real option for this field. Mirrors DynamicField select option resolution.
          const enumValues: unknown[] = Array.isArray(field.descriptor?.jsonSchema?.enum)
            ? (field.descriptor!.jsonSchema.enum as unknown[])
            : [];
          const options =
            field.descriptor?.options ?? enumValues.map((v) => ({ value: String(v) }));
          if (!options.some((o) => o.value === "0")) continue;
        }
        out[key] = value;
        break;
      }
      case "text":
      case "longtext":
      case "color": {
        const isEmpty = value === "" || value === null || value === undefined;
        if (isEmpty && !dirtyFields[key]) continue;
        out[key] = value;
        break;
      }
      default:
        out[key] = value;
    }
  }

  return out;
};
